import { useEffect, useState } from "react";
import { Building2, Plus, Pencil, Trash2, Users, GitBranch, Send, Loader2, Copy, FileDown, ListChecks, Sparkles } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Markdown } from "@/components/Markdown";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY_ORG = {
  nome: "", note: "", forma_giuridica: "", piva: "", codice_fiscale: "", numero_rea: "",
  ateco_primario: "", ateco_secondario: "", indirizzo: "", cap: "", comune: "", provincia: "",
  ref_titolo: "", ref_nome: "", ref_cognome: "", ref_ruolo: "", descrizione_attivita: "",
  sito_web: "", pec: "", norme_accreditate: [],
};

export default function Clients() {
  const { organizations, refreshOrgs } = useTenant();
  const { user } = useAuth();
  const isSuper = !!user?.super_admin;
  const [consultants, setConsultants] = useState([]);
  const [globalView, setGlobalView] = useState(!!user?.global_view);
  useEffect(() => { if (isSuper) api.get("/consultants").then((r) => setConsultants(r.data)).catch(() => {}); }, [isSuper]);
  const reassign = async (o, ownerId) => {
    try { await api.put(`/organizations/${o.id}/owner`, { owner_id: ownerId }); toast.success("Cliente riassegnato"); refreshOrgs(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleGlobal = async (v) => {
    setGlobalView(v);
    try { await api.put("/consultants/global-view", { enabled: v }); await refreshOrgs(); toast.success(v ? "Vista globale attiva" : "Vista globale disattivata"); }
    catch (e) { toast.error(apiError(e)); setGlobalView(!v); }
  };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ORG);
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(null);
  const [allNorme, setAllNorme] = useState([]);
  useEffect(() => { api.get("/norms").then((r) => setAllNorme(r.data)).catch(() => {}); }, []);
  const toggleFormNorma = (sigla) => {
    setForm((f) => {
      const cur = f.norme_accreditate || [];
      return { ...f, norme_accreditate: cur.includes(sigla) ? cur.filter((x) => x !== sigla) : [...cur, sigla] };
    });
  };

  const openNew = () => { setEditing(null); setForm(EMPTY_ORG); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...EMPTY_ORG, ...o }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/organizations/${editing.id}`, form);
      else await api.post("/organizations", form);
      toast.success("Cliente salvato");
      setOpen(false);
      refreshOrgs();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Eliminare "${o.nome}" e tutti i dati collegati?`)) return;
    try { await api.delete(`/organizations/${o.id}`); toast.success("Cliente eliminato"); refreshOrgs(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const [dossierId, setDossierId] = useState(null);
  const downloadDossier = async (o) => {
    setDossierId(o.id);
    try {
      const r = await api.get(`/clients/${o.id}/dossier`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `dossier_${o.nome}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Dossier generato");
    } catch (e) { toast.error("Generazione dossier non riuscita"); }
    finally { setDossierId(null); }
  };

  const [review, setReview] = useState({ open: false, loading: false, text: "", nome: "", id: "" });
  const verifyCoverage = async (o) => {
    setReview({ open: true, loading: true, text: "", nome: o.nome, id: o.id });
    try {
      const r = await api.post(`/clients/${o.id}/coverage-review`);
      if (r.data.status === "done") {
        setReview((s) => ({ ...s, loading: false, text: r.data.review }));
        return;
      }
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`/clients/${o.id}/coverage-review-status`);
          if (s.data.status === "done") {
            clearInterval(poll);
            setReview((st) => ({ ...st, loading: false, text: s.data.review }));
          } else if (s.data.status === "error") {
            clearInterval(poll);
            setReview((st) => ({ ...st, loading: false, text: "Verifica non riuscita. Riprova tra qualche istante." }));
          }
        } catch (e) { /* keep polling */ }
      }, 4000);
    } catch (e) { setReview((s) => ({ ...s, loading: false, text: "Verifica non riuscita." })); }
  };

  return (
    <div className="space-y-6">
      <PageHero label="Gestione" title="Clienti">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          {isSuper && (
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="global-view-toggle">
              <Checkbox checked={globalView} onCheckedChange={(v) => toggleGlobal(!!v)} />
              Vista globale (tutti i consulenti)
            </label>
          )}
          <Button onClick={openNew} data-testid="new-client-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
            <Plus className="h-4 w-4 mr-2" /> Nuovo cliente
          </Button>
        </div>
      </PageHero>

      {organizations.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessun cliente. Crea la prima organizzazione.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {organizations.map((o) => (
            <Card key={o.id} data-testid={`client-card-${o.id}`}
              className="p-5 border-slate-200 hover:-translate-y-1 hover:shadow-md transition-transform transition-shadow duration-200">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-blue-50 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-blue-700" />
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(o)} data-testid={`edit-client-${o.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(o)} data-testid={`delete-client-${o.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <h3 className="font-display font-semibold text-lg mt-4">{o.nome}</h3>
              <div className="mt-1 min-h-[20px] text-sm text-slate-500">
                {o.forma_giuridica && <span>{o.forma_giuridica}</span>}
                {o.piva && <span className="block text-xs text-slate-500">P.IVA {o.piva}{o.comune ? ` · ${o.comune}` : ""}</span>}
                {!o.forma_giuridica && !o.piva && o.note && <span className="line-clamp-2">{o.note}</span>}
              </div>
              {(o.norme_accreditate || []).length > 0 && (
                <div className="mt-3">
                  <div className="label-caps mb-1.5">Norme accreditate</div>
                  <div className="flex flex-wrap gap-1.5" data-testid={`client-norme-${o.id}`}>
                    {(o.norme_accreditate || []).map((sig) => (
                      <Badge key={sig} className="bg-indigo-100 text-indigo-700 border-indigo-200 font-mono text-[11px]">{sig}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {isSuper && (
                <div className="mt-4">
                  <div className="label-caps mb-1">Consulente assegnato</div>
                  <select value={o.owner_id || ""} onChange={(e) => reassign(o, e.target.value)}
                    data-testid={`reassign-${o.id}`}
                    className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    {!o.owner_id && <option value="">— Non assegnato —</option>}
                    {consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <Button variant="outline" className="w-full mt-4" onClick={() => setManage(o)} data-testid={`manage-client-${o.id}`}>
                Gestisci accessi e processi
              </Button>
              <Button variant="ghost" className="w-full mt-2 text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                onClick={() => downloadDossier(o)} disabled={dossierId === o.id} data-testid={`dossier-client-${o.id}`}>
                {dossierId === o.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
                Scarica dossier PDF
              </Button>
              <Button variant="ghost" className="w-full mt-1 text-indigo-700 hover:text-indigo-800 hover:bg-indigo-50"
                onClick={() => verifyCoverage(o)} data-testid={`verify-coverage-${o.id}`}>
                <Sparkles className="h-4 w-4 mr-2" /> Verifica copertura (Steve)
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div>
              <Label>Ragione sociale *</Label>
              <Input value={form.nome} data-testid="client-nome" onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Forma giuridica</Label><Input value={form.forma_giuridica} data-testid="client-forma" onChange={(e) => setForm({ ...form, forma_giuridica: e.target.value })} className="mt-1.5" /></div>
              <div><Label>P.IVA</Label><Input value={form.piva} data-testid="client-piva" onChange={(e) => setForm({ ...form, piva: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Codice fiscale</Label><Input value={form.codice_fiscale} data-testid="client-cf" onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Numero REA</Label><Input value={form.numero_rea} data-testid="client-rea" onChange={(e) => setForm({ ...form, numero_rea: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Codice ATECO (primario)</Label><Input value={form.ateco_primario} data-testid="client-ateco1" onChange={(e) => setForm({ ...form, ateco_primario: e.target.value })} className="mt-1.5" placeholder="Es. 41.00.00 — Costruzione di edifici" /></div>
              <div><Label>Codice ATECO secondario</Label><Input value={form.ateco_secondario} data-testid="client-ateco2" onChange={(e) => setForm({ ...form, ateco_secondario: e.target.value })} className="mt-1.5" /></div>
            </div>

            <div>
              <div className="label-caps mb-2">Sede legale</div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_1fr] gap-4">
                <div><Label>Indirizzo (via e n. civico)</Label><Input value={form.indirizzo} data-testid="client-indirizzo" onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} className="mt-1.5" /></div>
                <div><Label>CAP</Label><Input value={form.cap} data-testid="client-cap" onChange={(e) => setForm({ ...form, cap: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Comune</Label><Input value={form.comune} data-testid="client-comune" onChange={(e) => setForm({ ...form, comune: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div className="mt-4 md:w-1/2"><Label>Provincia</Label><Input value={form.provincia} data-testid="client-provincia" onChange={(e) => setForm({ ...form, provincia: e.target.value })} className="mt-1.5" placeholder="Es. CT · Catania" /></div>
            </div>

            <div>
              <div className="label-caps mb-2">Referente di sede legale</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><Label>Titolo</Label><Input value={form.ref_titolo} data-testid="client-ref-titolo" onChange={(e) => setForm({ ...form, ref_titolo: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Nome</Label><Input value={form.ref_nome} data-testid="client-ref-nome" onChange={(e) => setForm({ ...form, ref_nome: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Cognome</Label><Input value={form.ref_cognome} data-testid="client-ref-cognome" onChange={(e) => setForm({ ...form, ref_cognome: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Ruolo</Label><Input value={form.ref_ruolo} data-testid="client-ref-ruolo" onChange={(e) => setForm({ ...form, ref_ruolo: e.target.value })} className="mt-1.5" /></div>
              </div>
            </div>

            <div><Label>Descrizione attività prevalente</Label><Input value={form.descrizione_attivita} data-testid="client-attivita" onChange={(e) => setForm({ ...form, descrizione_attivita: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Sito web</Label><Input value={form.sito_web} data-testid="client-web" onChange={(e) => setForm({ ...form, sito_web: e.target.value })} className="mt-1.5" /></div>
              <div><Label>PEC</Label><Input value={form.pec} data-testid="client-pec" onChange={(e) => setForm({ ...form, pec: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div>
              <div className="label-caps mb-2">Norme accreditate</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {allNorme.map((n) => (
                  <label key={n.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 cursor-pointer hover:border-indigo-300" data-testid={`form-norma-${n.sigla}`} title={n.titolo}>
                    <Checkbox checked={(form.norme_accreditate || []).includes(n.sigla)} onCheckedChange={() => toggleFormNorma(n.sigla)} />
                    <span className="text-xs font-mono">{n.sigla}</span>
                  </label>
                ))}
                {allNorme.length === 0 && <span className="text-sm text-slate-500">Nessuna norma presente nelle Fonti.</span>}
              </div>
            </div>
            <div><Label>Note</Label><Textarea value={form.note} data-testid="client-note" onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-client-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {manage && <ManageDialog org={manage} onClose={() => setManage(null)} />}

      <Dialog open={review.open} onOpenChange={(o) => setReview((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="coverage-review-dialog">
          <DialogHeader className="flex-row items-center justify-between gap-4 pr-8">
            <DialogTitle className="text-base">Verifica copertura · {review.nome}</DialogTitle>
            {!review.loading && review.text && (
              <Button size="sm" variant="outline" data-testid="review-pdf-btn"
                onClick={() => window.open(`${API}/clients/${review.id}/coverage-review/report`, "_blank")}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 shrink-0">
                <FileDown className="h-4 w-4 mr-2" /> Scarica PDF
              </Button>
            )}
          </DialogHeader>
          {review.loading
            ? <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Steve sta analizzando la copertura...</div>
            : (review.text
                ? <Markdown>{review.text}</Markdown>
                : <div className="py-10 text-center text-slate-500">Nessun contenuto disponibile. Chiudi e riprova la verifica.</div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ManageDialog({ org, onClose }) {
  const [processes, setProcesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [procName, setProcName] = useState("");
  const [invite, setInvite] = useState({ email: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [schemeIds, setSchemeIds] = useState(org.scheme_ids || []);

  const load = () => {
    api.get(`/processes?tenant_id=${org.id}`).then((r) => setProcesses(r.data));
    api.get(`/users?tenant_id=${org.id}`).then((r) => setUsers(r.data));
    api.get(`/schemes`).then((r) => setSchemes(r.data)).catch(() => {});
  };
  useEffect(load, [org.id]);

  const toggleScheme = async (sid) => {
    const next = schemeIds.includes(sid) ? schemeIds.filter((x) => x !== sid) : [...schemeIds, sid];
    setSchemeIds(next);
    try { await api.put(`/organizations/${org.id}/schemes`, { scheme_ids: next }); toast.success("Schemi aggiornati"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addProcess = async () => {
    if (!procName.trim()) return;
    try { await api.post("/processes", { tenant_id: org.id, nome: procName }); setProcName(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delProcess = async (id) => { await api.delete(`/processes/${id}`); load(); };

  const sendInvite = async () => {
    setLoading(true);
    try {
      const r = await api.post("/users/invite", { ...invite, tenant_id: org.id });
      if (r.data.emailed) toast.success("Invito inviato via email");
      else toast.warning("Email non recapitabile: condividi il link manualmente");
      setLastInvite({ email: invite.email, url: r.data.invite_url, emailed: r.data.emailed });
      setInvite({ email: "", name: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const delUser = async (id) => { await api.delete(`/users/${id}`); load(); };
  const resend = async (id) => {
    try {
      const r = await api.post(`/users/${id}/resend-invite`);
      if (r.data.emailed) toast.success("Invito re-inviato");
      else { toast.warning("Email non recapitabile: usa il link"); setLastInvite({ url: r.data.invite_url, emailed: false }); }
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{org.nome}</DialogTitle></DialogHeader>
        <Tabs defaultValue="processi">
          <TabsList>
            <TabsTrigger value="processi" data-testid="tab-processi"><GitBranch className="h-4 w-4 mr-2" />Processi</TabsTrigger>
            <TabsTrigger value="utenti" data-testid="tab-utenti"><Users className="h-4 w-4 mr-2" />Utenti</TabsTrigger>
            <TabsTrigger value="schemi" data-testid="tab-schemi"><ListChecks className="h-4 w-4 mr-2" />Schemi</TabsTrigger>
          </TabsList>
          <TabsContent value="processi" className="space-y-3 pt-2">
            <div className="flex gap-2">
              <Input placeholder="Es. Acquisti" value={procName} data-testid="process-name"
                onChange={(e) => setProcName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addProcess()} />
              <Button onClick={addProcess} data-testid="add-process-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Aggiungi</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {processes.map((p) => (
                <Badge key={p.id} variant="outline" className="pl-3 pr-1 py-1 gap-1">
                  {p.nome}
                  <button onClick={() => delProcess(p.id)} className="ml-1 text-slate-500 hover:text-red-600" data-testid={`del-process-${p.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {processes.length === 0 && <span className="text-sm text-slate-500">Nessun processo definito.</span>}
            </div>
          </TabsContent>
          <TabsContent value="utenti" className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Email utente" value={invite.email} data-testid="invite-email"
                onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
              <Input placeholder="Nome (opzionale)" value={invite.name}
                onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
            </div>
            <Button onClick={sendInvite} disabled={loading || !invite.email} data-testid="send-invite-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Invita utente cliente
            </Button>
            {lastInvite && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="invite-link-box">
                <div className="text-xs text-slate-500">
                  {lastInvite.emailed ? "Invito inviato. Link di attivazione:" : "Email non recapitata. Condividi questo link di attivazione:"}
                </div>
                <div className="flex items-center gap-2">
                  <Input readOnly value={lastInvite.url} className="text-xs font-mono h-8" data-testid="invite-link-value" />
                  <Button size="icon" variant="outline" className="h-8 w-8 shrink-0"
                    onClick={() => { navigator.clipboard.writeText(lastInvite.url); toast.success("Link copiato"); }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-2 pt-2">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-2 border-b border-slate-100">
                  <div>
                    <div className="text-sm font-medium">{u.email}</div>
                    <Badge className={u.stato_invito === "active" ? "bg-blue-100 text-blue-800 border-blue-200 mt-1" : "bg-amber-100 text-amber-800 border-amber-200 mt-1"}>
                      {u.stato_invito === "active" ? "Attivo" : "Invito in sospeso"}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    {u.stato_invito !== "active" && (
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => resend(u.id)} title="Re-invia invito">
                        <Send className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => delUser(u.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {users.length === 0 && <span className="text-sm text-slate-500">Nessun utente invitato.</span>}
            </div>
          </TabsContent>
          <TabsContent value="schemi" className="space-y-2 pt-2">
            <p className="text-sm text-slate-500">Seleziona gli schemi/norme applicabili a {org.nome}. Copertura, gap, dossier e la verifica di Steve si concentreranno su questi.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
              {schemes.map((s) => (
                <label key={s.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 cursor-pointer hover:border-indigo-300" data-testid={`scheme-check-${s.id}`}>
                  <Checkbox checked={schemeIds.includes(s.id)} onCheckedChange={() => toggleScheme(s.id)} />
                  <span className="text-sm font-medium">{s.sigla || s.nome}</span>
                </label>
              ))}
              {schemes.length === 0 && <span className="text-sm text-slate-500">Nessuno schema definito.</span>}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
